/**
 * Client payment request checkout module
 *
 * Module for client to add card and checkout appt
 */

import ssFetch from '../ssFetch';

/**
 * Sends the payment request request
 * @param  {number} providerId - provider ID
 * @param  {number} appointmentId - appointment ID
 * @return {Promise}
 */
function sendRequestForPayment(providerId, appointmentId) {
  const url = `/api/v2/providers/${providerId}/appointments/${appointmentId}/send_appointment_client_payment_request`;
  return ssFetch(url, {
    method: 'POST',
    throwOnHttpError: true,
  }).then(res => res.json());
}

/**
 * Retrieve the appt information for checkout
 * @param {string} token - token to retrieve consent status and data
 * @return {object} appt detail object
 */
function getAppt(token) {
  const url = `/api/v1/pay/appointment_client_payment_request/detail?token=${token}`;
  return ssFetch(url, {
    throwOnHttpError: true,
  }).then(res => res.json());
}

/**
 * Update the client's email address
 * @param  {String} token - The client auth token
 * @param  {String} email - The new email address
 * @return {Promise}
 */
const updateEmailAddress = (token, email) => {
  const url = '/api/v1/pay/appointment_client_payment_request/update_email';
  const body = {
    token,
    email,
  };

  return ssFetch(url, {
    method: 'PUT',
    throwOnHttpError: true,
    body: JSON.stringify(body),
  }).then(res => res);
};

/**
 * Update the client's email address
 * @param  {String} clientToken - The client auth token
 * @param  {Object} instrumentToken - The card token from Stripe
 * @param  {Bool} saveForFuture - Boolean to save card to client wallet
 *                and give pro consent to use for future appts
 * @return {Promise}
 */
const addCard = (clientToken, instrumentToken, saveForFuture) => {
  const url = '/api/v1/pay/appointment_client_payment_request/add_card';
  const body = {
    settings_token: clientToken,
    token: instrumentToken,
    save_for_future: saveForFuture,
  };

  return ssFetch(url, {
    method: 'POST',
    body: JSON.stringify(body),
  }).then(res => {
    if (res.status === 201) {
      return res.json();
    }

    if (res.status === 400) {
      // stripe issue
      return res.json().then(error => {
        let errorMessage = 'Sorry - something went wrong. Please try again later.';
        if (error.length) {
          errorMessage = error[0];
        }
        // Stripe errors currently come with a request id which is irrelevant to users
        // This is being removed, but we'll have check for it for now
        // TODO: remove this parsing when the backend is fixed
        const matched = errorMessage.match(/req_[^:]+: (.*)/);
        errorMessage = matched && matched.length > 1 ? matched[1] : error;
        return Promise.reject(errorMessage);
      });
    }

    if (res.status === 403) {
      // bad client token
      // eslint-disable-next-line prefer-promise-reject-errors
      return Promise.reject(
        'Sorry - something went wrong. Please try again later.',
      );
    }

    return Promise.reject();
  });
};

/**
 * Checkout the appt
 * @param {string} clientToken - The client auth token
 * @param {number} instrumentId - The instrument Id
 * @param cvcToken
 * @param {number} tip - The tip amount
 * @param {number} tipPercentage - The tip percentage
 * @return {promise}
 */
const checkout = (clientToken, instrumentId, cvcToken, tip, tipPercentage) => {
  const url = '/api/v1/pay/appointment_client_payment_request/checkout';
  const body = {
    token: clientToken,
    tip_amount: tip,
    tip_percentage: tipPercentage,
    instrument_id: instrumentId,
  };

  if (cvcToken) {
    body.cvc_token = cvcToken;
  }

  return ssFetch(url, {
    method: 'POST',
    body: JSON.stringify(body),
  }).then(res => {
    if (res.status === 201) {
      return Promise.resolve(res);
    }
    return Promise.reject(res);
  });
};

export default {
  addCard,
  checkout,
  getAppt,
  sendRequestForPayment,
  updateEmailAddress,
};
